import { getRoot, } from 'mobx-state-tree';
import { intersection } from 'lodash-es';
import schema, { generateType, } from './Schema';
export const Meeting = generateType(schema.getModel('meeting')).volatile(() => ({
    layout: 'standard',
    mode: 'attendee',
    webAudioEnabled: true,
    videoTransformCpuUtilization: '40%',
})).views((self) => ({
    isCourse() {
        return false;
    },
    isDocument() {
        return false;
    },
    isProgram() {
        return false;
    },
    isMeeting() {
        return true;
    },
    isMicrocast() {
        return false;
    },
    itemImage() {
        return self.image;
    },
})).actions((self) => ({
    setLayout(newLayout) {
        self.layout = newLayout;
    },
    setMode(newMode) {
        self.mode = newMode;
    },
    setWebAudioEnabled(enabled) {
        self.webAudioEnabled = enabled;
    },
    setVideoTransformCpuUtilization(newValue) {
        self.videoTransformCpuUtilization = newValue;
    },
    /**
       * Determines if the current user has access to take this course. Access
       * is based on user's access groups, plus purchasedCourses, plus course
       * properties.
       * @param user
       */
    checkAccess() {
        // @TODO uses exact same logic as amplify/function/trackProgress.checkAccess
        // consolidate to one place.
        const rootModel = getRoot(self);
        const access = rootModel.currentAccess;
        if (self.restrictByAccessGroup) {
            if (!access) {
                return false;
            }
            const customerAccessGroups = access.groups || [];
            const commonGroups = intersection(self.accessGroups, customerAccessGroups);
            if (commonGroups.length > 0) {
                console.log('Access granted via accessGroups: ', commonGroups);
                return true;
            }
            return false;
        }
        if (self.restrictByCustomer) {
            const inList = self.customers?.find((c) => c.id === rootModel.currentCustomer?.id);
            return inList;
        }
        return true;
    },
}));
export const Registration = generateType(schema.getModel('meetingRegistration'));
export const createPortalMeetingGQL = (isPortalApi) => (`
mutation CreatePortalMeeting(
  ${!isPortalApi ? '$organizationID: String!' : ''}
  $portalID: String!
  $libraries: [ID]!
  $name: String!
) {
  createPortalMeeting(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}
    portalID: $portalID
    libraries: $libraries
    name: $name
  ) {
    ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
  }
}
`);
export const getMeetingRegistrationForCustomerGQL = (isPortalApi) => (`
query GetMeetingRegistrationForCustomer (
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
) {
  getMeetingRegistrationForCustomer(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
  ) {
    Meeting {
      ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
      ${chimeMeetingFragment}
    }
    Registration {
      ${schema.getGqlPropertyList('meetingRegistration', isPortalApi, true, false).join(' ')}
    }
    CurrentAttendee {
      AttendeeId
      ExternalUserId
      JoinToken
      __typename
    }
    Attendees {
      AttendeeId
      ExternalUserId
      JoinToken
      __typename
    }
    statusCode
    message
    __typename
  }
}
`);
export const getMeetingAttendeeInfoGQL = (isPortalApi) => (`
query getMeetingAttendeeInfo (
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
  $attendeeID: ID!
) {
  getMeetingAttendeeInfo(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
    attendeeID: $attendeeID
  ) {
    id name
  }
}
`);
export const registerForMeetingGQL = (isPortalApi) => (`
mutation RegisterForMeeting(
  ${!isPortalApi ? '$organizationID: String!' : ''}
  $portalID: String!
  $meetingKey: String!
) {
  registerForMeeting(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
  ) {
    statusCode
    Registration {
      ${schema.getGqlPropertyList('meetingRegistration', isPortalApi, true, false).join(' ')}
    }
    message
  }
}
`);
const chimeMeetingFragment = `
chimeMeeting {
  ExternalMeetingId
  MediaRegion
  MediaPlacement {
    AudioHostUrl
    AudioFallbackUrl
    ScreenDataUrl
    ScreenSharingUrl
    ScreenViewingUrl
    SignalingUrl
    TurnControlUrl
  }
  MeetingArn
  MeetingId
}
`;
export const joinMeetingGQL = (isPortalApi) => ( /* GraphQL */`
mutation JoinMeeting(
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
  $attendeeName: String
) {
  joinMeeting(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
    attendeeName: $attendeeName
  ) {
    Meeting {
      ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
      ${chimeMeetingFragment}
    }
    Registration {
      ${schema.getGqlPropertyList('meetingRegistration', isPortalApi, true, false).join(' ')}
    }
    CurrentAttendee {
      AttendeeId
      ExternalUserId
      JoinToken
      __typename
    }
    Attendees {
      AttendeeId
      ExternalUserId
      JoinToken
      __typename
    }
    statusCode
    message
    __typename
  }
}`);
export const startMeetingGQL = (isPortalApi) => ( /* GraphQL */`
mutation StartMeeting(
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $meetingKey: String!
) {
  startMeeting(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    meetingKey: $meetingKey
  ) {
    Meeting {
      ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
      ${chimeMeetingFragment}
    }
    statusCode
    message
    __typename
  }
}`);
export const endMeetingGQL = (isPortalApi) => ( /* GraphQL */`
mutation endMeeting(
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
) {
  endMeeting(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
  ) {
    Meeting {
      ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
      ${chimeMeetingFragment}
    }
    Registration {
      ${schema.getGqlPropertyList('meetingRegistration', isPortalApi, true, false).join(' ')}
    }
    statusCode
    message
    __typename
  }
}`);
export const startMeetingRecordingGQL = (isPortalApi) => ( /* GraphQL */`
mutation startMeetingRecording(
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
) {
  startMeetingRecording(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
  ) {
    Meeting {
      ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
    }
    statusCode
    message
    __typename
  }
}`);
export const stopMeetingRecordingGQL = (isPortalApi) => ( /* GraphQL */`
mutation stopMeetingRecording(
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
) {
  stopMeetingRecording(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
  ) {
    Meeting {
      ${schema.getGqlPropertyList('meeting', isPortalApi, true, false).join(' ')}
    }
    statusCode
    message
    __typename
  }
}`);
export const sendMeetingInvitesGQL = (isPortalApi) => ( /* GraphQL */`
mutation SendMeetingInvites(
  ${!isPortalApi ? '$organizationID: ID!' : ''}
  $portalID: ID!
  $meetingKey: ID!
  $subject: String
  $message: String
  $emailAddresses: [String!]!
) {
  sendMeetingInvites(
    ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    portalID: $portalID
    meetingKey: $meetingKey
    subject: $subject
    message: $message
    emailAddresses: $emailAddresses
  ) {
    statusCode
    message
    __typename
  }
}`);
//# sourceMappingURL=MeetingModel.js.map